import type { Layer, Project, Transform } from './schema';

export function createId(prefix = 'id'): string {
  return `${prefix}_${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
}

export const defaultTransform = (position: [number, number, number] = [0, 0, 0]): Transform => ({
  position,
  scale: [1, 1, 1],
  rotation: [0, 0, 0],
  anchor: [0, 0, 0],
  opacity: 1
});

export function createLayer(project: Project, input: Partial<Layer> & Pick<Layer, 'type' | 'name'>): Layer {
  const composition = project.compositions[project.activeCompositionId];
  const { name, type, ...overrides } = input;
  return {
    id: createId('layer'),
    name,
    type,
    compositionId: composition.id,
    parentId: null,
    visible: true,
    locked: false,
    muted: false,
    solo: false,
    blendMode: 'normal',
    start: 0,
    duration: Math.min(5, composition.duration),
    inPoint: 0,
    speed: 1,
    reverse: false,
    transform: defaultTransform(),
    keyframes: {},
    effects: [],
    masks: [],
    volume: 1,
    fadeIn: 0,
    fadeOut: 0,
    ...overrides
  };
}

export function createProject(name = 'Untitled Project', width = 1920, height = 1080, fps = 30): Project {
  const projectId = createId('project');
  const compositionId = createId('comp');
  const now = Date.now();
  const project: Project = {
    schemaVersion: 1,
    id: projectId,
    name,
    createdAt: now,
    updatedAt: now,
    activeCompositionId: compositionId,
    compositions: {
      [compositionId]: {
        id: compositionId,
        name: 'Main',
        width,
        height,
        fps,
        duration: 30,
        backgroundColor: '#17191e',
        layerIds: [],
        markers: [{ id: createId('marker'), time: 5, label: 'Intro ends', color: '#e6ff55' }],
        parentCompositionId: null
      }
    },
    layers: {},
    assets: {},
    metadata: { author: '', description: '', appVersion: '0.1.0' }
  };

  const background = createLayer(project, {
    name: 'Background', type: 'shape', duration: 30, color: '#202329', transform: defaultTransform(), locked: true
  });
  const accent = createLayer(project, {
    name: 'Accent bar', type: 'shape', start: 1, duration: 7, color: '#e6ff55', transform: { ...defaultTransform([0, 165, 0]), scale: [0.24, 0.025, 1] },
    keyframes: { 'transform.scale': [
      { id: createId('kf'), time: 0, value: [0, 0.025, 1], interpolation: 'ease-out' },
      { id: createId('kf'), time: 0.65, value: [0.24, 0.025, 1], interpolation: 'ease-out' }
    ] }
  });
  const title = createLayer(project, {
    name: 'OPEN MOTION', type: 'text', start: 1, duration: 7, text: 'OPEN MOTION', fontSize: 104, fontFamily: 'Inter, sans-serif', fontWeight: 750, color: '#f4f5f2',
    keyframes: {
      'transform.position': [
        { id: createId('kf'), time: 0, value: [0, 65, 0], interpolation: 'ease-out' },
        { id: createId('kf'), time: 0.7, value: [0, 0, 0], interpolation: 'ease-out' }
      ],
      'transform.opacity': [
        { id: createId('kf'), time: 0, value: 0, interpolation: 'linear' },
        { id: createId('kf'), time: 0.45, value: 1, interpolation: 'linear' }
      ]
    }
  });
  const subtitle = createLayer(project, {
    name: 'Browser-native creative workstation', type: 'text', start: 1.35, duration: 6.65, text: 'VIDEO  /  MOTION  /  3D', fontSize: 30, fontFamily: 'Inter, sans-serif', fontWeight: 600, color: '#a9adb3',
    transform: defaultTransform([0, 92, 0])
  });

  for (const layer of [background, accent, title, subtitle]) project.layers[layer.id] = layer;
  project.compositions[compositionId].layerIds = [subtitle.id, title.id, accent.id, background.id];
  return project;
}

export function activeComposition(project: Project) {
  return project.compositions[project.activeCompositionId];
}

export function serializeProject(project: Project): string {
  return JSON.stringify({ ...project, updatedAt: Date.now() });
}
